import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Row, Col, Badge, Button, Modal, Form, Alert, Table } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import AttachmentList from '../components/AttachmentList.jsx';
import { fmtMoney, fmtDate, fmtDateTime, STATUS_BADGE, PAYMENT_METHOD_LABEL } from '../lib/format.js';

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get(`/invoices/${id}`).then(r => r.data)
  });
  const customers = useQuery({
    queryKey: ['customers-min'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(r => r.data.items)
  });

  // ---- Item editor ----
  const [itemModal, setItemModal] = useState(false);
  const [itemDraft, setItemDraft] = useState({ id: null, description: '', quantity: 1, unit: '', unit_price: '' });
  const openItem = (it) => {
    setItemDraft(it
      ? { id: it.id, description: it.description, quantity: it.quantity, unit: it.unit || '', unit_price: (it.unit_price_cents / 100).toString() }
      : { id: null, description: '', quantity: 1, unit: '', unit_price: '' });
    setItemModal(true);
  };

  const saveItem = useMutation({
    mutationFn: () => {
      const body = {
        description: itemDraft.description,
        quantity: parseFloat(itemDraft.quantity),
        unit: itemDraft.unit || null,
        unit_price_cents: Math.round(parseFloat(itemDraft.unit_price) * 100)
      };
      return itemDraft.id
        ? api.patch(`/invoices/${id}/items/${itemDraft.id}`, body).then(r => r.data)
        : api.post(`/invoices/${id}/items`, body).then(r => r.data);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); setItemModal(false); }
  });
  const removeItem = useMutation({
    mutationFn: (itemId) => api.delete(`/invoices/${id}/items/${itemId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoice', id] })
  });

  // ---- Issue / cancel ----
  const issue = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/issue`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoice', id] })
  });
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const cancel = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/cancel`, { reason: cancelReason || null }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); setCancelModal(false); }
  });

  // ---- Header (customer / dates / VAT) ----
  const meta = useMutation({
    mutationFn: (patch) => api.patch(`/invoices/${id}`, patch).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoice', id] })
  });

  // ---- Payment ----
  const [payModal, setPayModal] = useState(false);
  const [payDraft, setPayDraft] = useState({ amount: '', method: 'bank_transfer', paid_at: '', reference: '', note: '' });
  const recordPayment = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/payments`, {
      amount_cents: Math.round(parseFloat(payDraft.amount) * 100),
      method: payDraft.method,
      paid_at: payDraft.paid_at ? new Date(payDraft.paid_at).toISOString() : null,
      reference: payDraft.reference || null,
      note: payDraft.note || null
    }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); setPayModal(false); setPayDraft({ amount: '', method: 'bank_transfer', paid_at: '', reference: '', note: '' }); }
  });
  const revertPayment = useMutation({
    mutationFn: (pid) => api.post(`/payments/${pid}/revert`, { reason: 'Manuell rückgebucht' }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoice', id] })
  });

  // ---- Dunning ----
  const advanceDunning = useMutation({
    mutationFn: (sendEmail) => api.post(`/invoices/${id}/dunning/advance`, { send_email: !!sendEmail }).then(r => r.data),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      if (r.email) setEmailResult(r.email.ok ? { ok: true } : { ok: false, ...r.email });
    }
  });

  // ---- PDF / XRechnung / Email ----
  const [emailResult, setEmailResult] = useState(null);

  const downloadFile = async (kind) => {
    const r = await api.get(`/invoices/${id}/${kind}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    if (kind === 'pdf') {
      window.open(url, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${detail.data?.invoice?.invoice_number || 'invoice'}.${kind === 'xrechnung.xml' ? 'xml' : kind}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const sendByEmail = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/email`).then(r => r.data),
    onSuccess: (r) => setEmailResult(r),
    onError: (e) => setEmailResult({ ok: false, error: e.response?.data?.code || e.message })
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/invoices/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); navigate('/invoices'); },
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  if (detail.isLoading) return <div className="text-muted">Lade…</div>;
  if (detail.error) return <Alert variant="danger">Rechnung nicht gefunden.</Alert>;

  const inv = detail.data.invoice;
  const items = detail.data.items || [];
  const payments = detail.data.payments || [];
  const dunning = detail.data.dunning_events || [];
  const customer = (customers.data || []).find(c => c.id === inv.customer_id);
  const sb = STATUS_BADGE[inv.status] || { variant: 'secondary', label: inv.status };
  const isDraft = inv.status === 'draft';
  const isCancelled = inv.status === 'cancelled';
  const canPay = inv.status === 'issued' || inv.status === 'overdue';

  const fillRest = () => {
    setPayDraft({ ...payDraft, amount: ((inv.total_cents - inv.paid_cents) / 100).toFixed(2) });
  };

  return (
    <>
      <PageHeader
        title={inv.invoice_number || 'Rechnungs-Entwurf'}
        subtitle={
          <span>
            <Badge bg="" className={`badge-soft-${sb.variant} me-2`}>{sb.label}</Badge>
            {customer && <Link to={`/customers/${inv.customer_id}`} className="text-decoration-none">{customer.name}</Link>}
          </span>
        }
        actions={
          <>
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              <Icon icon="tabler:arrow-left" className="me-1" />Zurück
            </Button>
            {!isDraft && (
              <>
                <Button variant="outline-secondary" onClick={() => downloadFile('pdf')} title="PDF">
                  <Icon icon="tabler:file-type-pdf" className="me-1" />PDF
                </Button>
                <Button variant="outline-secondary" onClick={() => downloadFile('xrechnung.xml')} title="XRechnung">
                  <Icon icon="tabler:file-code" className="me-1" />XRechnung
                </Button>
                <Button variant="outline-secondary" onClick={() => sendByEmail.mutate()}
                  disabled={sendByEmail.isLoading} title="Per E-Mail an den Kunden">
                  <Icon icon="tabler:mail" className="me-1" />
                  {sendByEmail.isLoading ? 'Sende…' : 'E-Mail'}
                </Button>
              </>
            )}
            {isDraft && auth.can('invoices', 'issue') && (
              <Button variant="success" onClick={() => issue.mutate()} disabled={items.length === 0}>
                <Icon icon="tabler:circle-check" className="me-1" />Ausstellen
              </Button>
            )}
            {isDraft && auth.can('invoices', 'delete') && (
              <Button variant="outline-danger"
                onClick={() => confirm('Entwurf endgültig löschen?') && del.mutate()}
                disabled={del.isLoading}>
                <Icon icon="tabler:trash" className="me-1" />Entwurf löschen
              </Button>
            )}
            {isCancelled && auth.can('invoices', 'delete') && (
              <Button variant="outline-danger"
                onClick={() => confirm('Stornierte Rechnung endgültig löschen?\n\nDer Datensatz wird aus der DB entfernt — die Storno-Spur im Audit-Log bleibt erhalten.') && del.mutate()}
                disabled={del.isLoading}>
                <Icon icon="tabler:trash" className="me-1" />Stornierte Rechnung löschen
              </Button>
            )}
            {!isDraft && !isCancelled && auth.can('invoices', 'cancel') && (
              <Button variant="outline-danger" onClick={() => setCancelModal(true)}>
                <Icon icon="tabler:circle-x" className="me-1" />Stornieren
              </Button>
            )}
          </>
        }
      />
      {emailResult && (
        <Alert variant={emailResult.ok ? 'success' : 'warning'} className="py-2 small" dismissible
          onClose={() => setEmailResult(null)}>
          {emailResult.ok
            ? `E-Mail an ${emailResult.to || 'Kunden'} gesendet.`
            : `E-Mail-Versand fehlgeschlagen: ${emailResult.reason || emailResult.error || 'Unbekannt'}`}
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={8}>
          {/* Items */}
          <Card className="card-flat mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-uppercase text-muted small mb-0" style={{ letterSpacing: '0.05em' }}>Positionen</h6>
                {isDraft && auth.can('invoices', 'update') && (
                  <Button size="sm" onClick={() => openItem(null)}><Icon icon="tabler:plus" className="me-1" />Position</Button>
                )}
              </div>
              {items.length === 0 ? (
                <div className="text-muted">Keine Positionen.</div>
              ) : (
                <Table className="mb-0 align-middle">
                  <thead><tr>
                    <th style={{ width: 30 }}>#</th>
                    <th>Beschreibung</th>
                    <th className="text-end" style={{ width: 100 }}>Menge</th>
                    <th className="text-end" style={{ width: 130 }}>Einzelpreis</th>
                    <th className="text-end" style={{ width: 130 }}>Gesamt</th>
                    {isDraft && <th style={{ width: 80 }}></th>}
                  </tr></thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={it.id}>
                        <td className="text-muted">{idx + 1}</td>
                        <td>{it.description}</td>
                        <td className="text-end">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</td>
                        <td className="text-end">{fmtMoney(it.unit_price_cents, inv.currency)}</td>
                        <td className="text-end fw-semibold">{fmtMoney(it.line_total_cents, inv.currency)}</td>
                        {isDraft && (
                          <td className="text-end pe-2">
                            <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openItem(it)}>
                              <Icon icon="tabler:edit" />
                            </Button>
                            <Button size="sm" variant="outline-danger"
                              onClick={() => confirm('Position löschen?') && removeItem.mutate(it.id)}>
                              <Icon icon="tabler:trash" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Attachments */}
          <Card className="card-flat mb-3">
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
                Anhänge / Belege
              </h6>
              <AttachmentList parentType="invoice" parentId={inv.id}
                items={detail.data.attachments || []}
                onChange={() => qc.invalidateQueries({ queryKey: ['invoice', id] })} />
            </Card.Body>
          </Card>

          {/* Payments + Dunning */}
          <Row className="g-3">
            <Col md={6}>
              <Card className="card-flat h-100"><Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="text-uppercase text-muted small mb-0">Zahlungen</h6>
                  {canPay && auth.can('payments', 'create') && (
                    <Button size="sm" onClick={() => { setPayModal(true); fillRest(); }}>
                      <Icon icon="tabler:cash" className="me-1" />Zahlung erfassen
                    </Button>
                  )}
                </div>
                {payments.length === 0 ? (
                  <div className="text-muted small">Keine Zahlungen erfasst.</div>
                ) : payments.map(p => (
                  <div key={p.id} className="d-flex justify-content-between align-items-start py-2 border-bottom">
                    <div>
                      <div>
                        <strong className={p.status === 'reverted' ? 'text-decoration-line-through text-muted' : ''}>
                          {fmtMoney(p.amount_cents, p.currency)}
                        </strong>
                        <code className="small text-muted ms-2">{p.payment_number}</code>
                      </div>
                      <div className="small text-muted">
                        {fmtDate(p.paid_at)} · {PAYMENT_METHOD_LABEL[p.method] || p.method}
                        {p.reference && ` · ${p.reference}`}
                      </div>
                      {p.status === 'reverted' && <Badge bg="" className="badge-soft-danger">Storniert</Badge>}
                    </div>
                    {p.status === 'confirmed' && auth.can('payments', 'revert') && (
                      <Button size="sm" variant="outline-secondary"
                        onClick={() => confirm('Zahlung wirklich rückbuchen?') && revertPayment.mutate(p.id)}>
                        <Icon icon="tabler:rotate" />
                      </Button>
                    )}
                  </div>
                ))}
              </Card.Body></Card>
            </Col>

            <Col md={6}>
              <Card className="card-flat h-100"><Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="text-uppercase text-muted small mb-0">Mahnstufen</h6>
                  {(inv.status === 'overdue' || inv.status === 'issued') && auth.can('dunning', 'advance') && (
                    <Button size="sm" variant="warning"
                      onClick={() => confirm('Nächste Mahnstufe wirklich auslösen?') && advanceDunning.mutate()}>
                      <Icon icon="tabler:bell-ringing" className="me-1" />Mahnen
                    </Button>
                  )}
                </div>
                {dunning.length === 0 ? (
                  <div className="text-muted small">Keine Mahnungen versendet.</div>
                ) : dunning.map(d => (
                  <div key={d.id} className="py-2 border-bottom">
                    <div className="d-flex justify-content-between">
                      <div>
                        <Badge bg="" className="badge-soft-warning me-2">Stufe {d.level}</Badge>
                        <span className="small text-muted">{fmtDateTime(d.sent_at)}</span>
                      </div>
                      <div className="small">
                        Gebühr: {fmtMoney(d.fee_cents, inv.currency)}
                      </div>
                    </div>
                    <div className="small text-muted">Neue Fälligkeit: {fmtDate(d.new_due_date)}</div>
                  </div>
                ))}
              </Card.Body></Card>
            </Col>
          </Row>
        </Col>

        {/* Sidebar — totals + meta */}
        <Col lg={4}>
          <Card className="card-flat mb-3"><Card.Body>
            <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>Summe</h6>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Zwischensumme</span><span>{fmtMoney(inv.subtotal_cents, inv.currency)}</span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">MwSt. ({inv.vat_rate}%)</span><span>{fmtMoney(inv.vat_cents, inv.currency)}</span>
            </div>
            {inv.dunning_fees_cents > 0 && (
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Mahngebühren</span><span>{fmtMoney(inv.dunning_fees_cents, inv.currency)}</span>
              </div>
            )}
            <hr />
            <div className="d-flex justify-content-between mb-1">
              <strong>Brutto</strong><strong>{fmtMoney(inv.total_cents, inv.currency)}</strong>
            </div>
            <div className="d-flex justify-content-between mb-1 small">
              <span className="text-muted">Bezahlt</span>
              <span className="text-success">{fmtMoney(inv.paid_cents, inv.currency)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <strong>Offen</strong>
              <strong className={inv.status === 'overdue' ? 'text-warning' : ''}>
                {fmtMoney(inv.total_cents - inv.paid_cents, inv.currency)}
              </strong>
            </div>
          </Card.Body></Card>

          <Card className="card-flat"><Card.Body>
            <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>Eckdaten</h6>
            <Row className="g-2">
              <Col xs={5} className="text-muted small">Rechnungs-Nr.</Col>
              <Col xs={7}>{inv.invoice_number || <span className="text-muted">—</span>}</Col>
              <Col xs={5} className="text-muted small">Rechnungsdatum</Col>
              <Col xs={7}>{inv.issue_date ? fmtDate(inv.issue_date) : '—'}</Col>
              <Col xs={5} className="text-muted small">Fällig am</Col>
              <Col xs={7}>
                {isDraft ? (
                  <Form.Control size="sm" type="date"
                    value={inv.due_date || ''}
                    onChange={(e) => meta.mutate({ due_date: e.target.value || null })} />
                ) : inv.due_date ? fmtDate(inv.due_date) : '—'}
              </Col>
              <Col xs={5} className="text-muted small">MwSt.-Satz</Col>
              <Col xs={7}>
                {isDraft ? (
                  <Form.Control size="sm" type="number" min="0" max="100" step="0.5"
                    defaultValue={inv.vat_rate}
                    onBlur={(e) => meta.mutate({ vat_rate: parseFloat(e.target.value) || 0 })} />
                ) : `${inv.vat_rate} %`}
              </Col>
              <Col xs={5} className="text-muted small">Referenz</Col>
              <Col xs={7}>
                {isDraft ? (
                  <Form.Control size="sm" defaultValue={inv.reference || ''}
                    onBlur={(e) => meta.mutate({ reference: e.target.value || null })} />
                ) : (inv.reference || '—')}
              </Col>
            </Row>

            <h6 className="text-uppercase text-muted small mb-2 mt-3" style={{ letterSpacing: '0.05em' }}>Notiz</h6>
            {isDraft ? (
              <Form.Control as="textarea" rows={3} size="sm" defaultValue={inv.note || ''}
                onBlur={(e) => meta.mutate({ note: e.target.value || null })} />
            ) : <div className="text-muted small">{inv.note || '—'}</div>}
          </Card.Body></Card>
        </Col>
      </Row>

      {/* ---- Item modal ---- */}
      <Modal show={itemModal} onHide={() => setItemModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>{itemDraft.id ? 'Position bearbeiten' : 'Neue Position'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Beschreibung *</Form.Label>
            <Form.Control as="textarea" rows={2} value={itemDraft.description}
              onChange={(e) => setItemDraft({ ...itemDraft, description: e.target.value })}
              placeholder="z.B. Lizenzgebühr Q1 2026" />
          </Form.Group>
          <Row className="g-2">
            <Col xs={4}>
              <Form.Group>
                <Form.Label>Menge</Form.Label>
                <Form.Control type="number" step="0.01" min="0" value={itemDraft.quantity}
                  onChange={(e) => setItemDraft({ ...itemDraft, quantity: e.target.value })} />
              </Form.Group>
            </Col>
            <Col xs={3}>
              <Form.Group>
                <Form.Label>Einheit</Form.Label>
                <Form.Control value={itemDraft.unit}
                  onChange={(e) => setItemDraft({ ...itemDraft, unit: e.target.value })}
                  placeholder="Std., Stk." />
              </Form.Group>
            </Col>
            <Col xs={5}>
              <Form.Group>
                <Form.Label>Einzelpreis (netto, {inv.currency})</Form.Label>
                <Form.Control type="number" step="0.01" value={itemDraft.unit_price}
                  onChange={(e) => setItemDraft({ ...itemDraft, unit_price: e.target.value })}
                  placeholder="0.00" />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setItemModal(false)}>Abbrechen</Button>
          <Button onClick={() => saveItem.mutate()}
            disabled={!itemDraft.description || !itemDraft.unit_price}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- Payment modal ---- */}
      <Modal show={payModal} onHide={() => setPayModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Zahlung erfassen</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Betrag ({inv.currency})</Form.Label>
                <Form.Control type="number" step="0.01" value={payDraft.amount}
                  onChange={(e) => setPayDraft({ ...payDraft, amount: e.target.value })} />
                <Form.Text>Offen: {fmtMoney(inv.total_cents - inv.paid_cents, inv.currency)}
                  &nbsp;<a href="#" onClick={(e) => { e.preventDefault(); fillRest(); }}>komplett übernehmen</a>
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Zahlungsdatum</Form.Label>
                <Form.Control type="date" value={payDraft.paid_at}
                  onChange={(e) => setPayDraft({ ...payDraft, paid_at: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Zahlungsart</Form.Label>
                <Form.Select value={payDraft.method}
                  onChange={(e) => setPayDraft({ ...payDraft, method: e.target.value })}>
                  {Object.entries(PAYMENT_METHOD_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Referenz</Form.Label>
                <Form.Control value={payDraft.reference}
                  onChange={(e) => setPayDraft({ ...payDraft, reference: e.target.value })}
                  placeholder="Verwendungszweck / Tx-ID" />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Notiz</Form.Label>
                <Form.Control as="textarea" rows={2} value={payDraft.note}
                  onChange={(e) => setPayDraft({ ...payDraft, note: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPayModal(false)}>Abbrechen</Button>
          <Button onClick={() => recordPayment.mutate()} disabled={!payDraft.amount || parseFloat(payDraft.amount) <= 0}>
            Erfassen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- Cancel modal ---- */}
      <Modal show={cancelModal} onHide={() => setCancelModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Rechnung stornieren</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="py-2">Stornierung ist endgültig. Für eine bezahlte Rechnung ist statt der Stornierung eine Storno-Rechnung anzulegen.</Alert>
          <Form.Group>
            <Form.Label>Grund</Form.Label>
            <Form.Control as="textarea" rows={2} value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setCancelModal(false)}>Abbrechen</Button>
          <Button variant="danger" onClick={() => cancel.mutate()}>Endgültig stornieren</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
